import { lazy, Suspense, useState } from 'react';
import { AppShell, Box, Group, Loader, Text } from '@mantine/core';
import { Sidebar } from './components/Sidebar';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Dashboard } from './pages/Dashboard';

// Route-level code splitting: Dashboard is eager (the landing page); every
// other page lazy-loads on first navigation so the initial bundle stays small.
const PrelanderLibrary = lazy(() => import('./pages/PrelanderLibrary').then(m => ({ default: m.PrelanderLibrary })));
const PrelanderEditor  = lazy(() => import('./pages/PrelanderEditor' ).then(m => ({ default: m.PrelanderEditor  })));
const Push             = lazy(() => import('./pages/Push'            ).then(m => ({ default: m.Push             })));
const Domains          = lazy(() => import('./pages/Domains'         ).then(m => ({ default: m.Domains          })));
const Funnel           = lazy(() => import('./pages/Funnel'          ).then(m => ({ default: m.Funnel           })));
const Reports          = lazy(() => import('./pages/Reports'         ).then(m => ({ default: m.Reports          })));
const Settings         = lazy(() => import('./pages/Settings'        ).then(m => ({ default: m.Settings         })));
const Apps             = lazy(() => import('./pages/Apps'            ).then(m => ({ default: m.Apps             })));
const AppWizard        = lazy(() => import('./pages/AppWizard'       ).then(m => ({ default: m.AppWizard        })));
const AppDetail        = lazy(() => import('./pages/AppDetail'       ).then(m => ({ default: m.AppDetail        })));

type Route =
  | { name: 'dashboard' }
  | { name: 'apps' }
  | { name: 'app-new' }
  | { name: 'app';      id: string }
  | { name: 'prelanders' }
  | { name: 'prelander'; id: string }
  | { name: 'push' }
  | { name: 'domains' }
  | { name: 'funnel' }
  | { name: 'reports' }
  | { name: 'settings' }
  | { name: 'placeholder'; id: string };

export function App() {
  const [route, setRoute] = useState<Route>({ name: 'dashboard' });

  const activeSidebarId =
    route.name === 'dashboard' ? 'dashboard'
    : route.name === 'apps' || route.name === 'app-new' || route.name === 'app' ? 'apps'
    : route.name === 'prelanders' || route.name === 'prelander' ? 'prelanders'
    : route.name === 'push' ? 'push'
    : route.name === 'domains' ? 'domains'
    : route.name === 'funnel' ? 'funnel'
    : route.name === 'reports' ? 'reports'
    : route.name === 'settings' ? 'settings'
    : route.name === 'placeholder' ? route.id
    : '';

  function navigate(sidebarId: string) {
    switch (sidebarId) {
      case 'dashboard':  setRoute({ name: 'dashboard'  }); break;
      case 'apps':       setRoute({ name: 'apps'       }); break;
      case 'prelanders': setRoute({ name: 'prelanders' }); break;
      case 'push':       setRoute({ name: 'push'       }); break;
      case 'domains':    setRoute({ name: 'domains'    }); break;
      case 'funnel':     setRoute({ name: 'funnel'     }); break;
      case 'reports':    setRoute({ name: 'reports'    }); break;
      case 'settings':   setRoute({ name: 'settings'   }); break;
      default:           setRoute({ name: 'placeholder', id: sidebarId });
    }
  }

  return (
    <AppShell padding={0} styles={{ main: { background: '#f6f8fb' } }}>
      <AppShell.Main>
        <Group align="stretch" gap={0} mih="100vh" wrap="nowrap">
          <Sidebar active={activeSidebarId} onSelect={navigate} />
          <Box style={{ flex: 1, padding: '28px 32px', minWidth: 0, overflow: 'auto' }}>
            <ErrorBoundary resetKey={routeKey(route)}>
            <Suspense fallback={<Center><Loader size="sm" /></Center>}>
              {route.name === 'dashboard'  && <Dashboard />}
              {route.name === 'apps'       && <Apps onNew={() => setRoute({ name: 'app-new' })} onOpen={(id) => setRoute({ name: 'app', id })} />}
              {route.name === 'app-new'    && <AppWizard onBack={() => setRoute({ name: 'apps' })} onCreated={(id) => setRoute({ name: 'app', id })} />}
              {route.name === 'app'        && <AppDetail id={route.id} onBack={() => setRoute({ name: 'apps' })} onDeleted={() => setRoute({ name: 'apps' })} />}
              {route.name === 'prelanders' && <PrelanderLibrary onOpen={(id) => setRoute({ name: 'prelander', id })} />}
              {route.name === 'prelander'  && <PrelanderEditor id={route.id} onBack={() => setRoute({ name: 'prelanders' })} />}
              {route.name === 'push'       && <Push />}
              {route.name === 'domains'    && <Domains />}
              {route.name === 'funnel'     && <Funnel />}
              {route.name === 'reports'    && <Reports />}
              {route.name === 'settings'   && <Settings />}
              {route.name === 'placeholder' && (
                <Text c="dimmed">"{route.id}" not implemented yet.</Text>
              )}
            </Suspense>
            </ErrorBoundary>
          </Box>
        </Group>
      </AppShell.Main>
    </AppShell>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <Box style={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
      {children}
    </Box>
  );
}

// Stable route discriminator so ErrorBoundary resets when the user navigates
// to a new page (the error is page-local, not app-wide).
function routeKey(route: Route): string {
  if (route.name === 'prelander' || route.name === 'app' || route.name === 'placeholder') {
    return `${route.name}:${route.id}`;
  }
  return route.name;
}
